/**
 * @namespace ThemeEvents
 * @description A collection of theme-specific events that can be used to trigger and listen for changes anywhere in the theme.
 * @example
 * document.dispatchEvent(new VariantUpdateEvent(variant, sectionId, { html }));
 * document.addEventListener(ThemeEvents.variantUpdate, (e) => { console.log(e.detail.variant) });
 */
export class ThemeEvents {
	/** @static @constant {string} Event triggered when a variant is selected */
	static variantSelected = "variant:selected";
	/** @static @constant {string} Event triggered when a variant is changed */
	static variantUpdate = "variant:update";
	/** @static @constant {string} Event triggered when the cart items or quantities are updated */
	static cartUpdate = "cart:update";
	/** @static @constant {string} Event triggered when a cart error occurs */
	static cartError = "cart:error";
	/** @static @constant {string} Event triggered when a discount is applied */
	static discountUpdate = "discount:update";
	/** @static @constant {string} Event triggered when a product is added to the compare list */
	static compareAdd = "compare:add";
	/** @static @constant {string} Event triggered when a product is removed from the compare list */
	static compareRemove = "compare:remove";
	/** @static @constant {string} Event triggered when the compare mode is activated */
	static compareActivate = "compare:activate";
	/** @static @constant {string} Event triggered when the compare mode is deactivated */
	static compareDeactivate = "compare:deactivate";
	/** @static @constant {string} Event triggered when a lazy video is loaded */
	static lazyVideoLoaded = "lazy-video:loaded";
	/** @static @constant {string} Event triggered when the wishlist is updated */
	static wishlistUpdate = "wishlist:update";
	/** @static @constant {string} Event triggered when a product is added to the wishlist */
	static wishlistAdd = "wishlist:add";
	/** @static @constant {string} Event triggered when a product is removed from the wishlist */
	static wishlistRemove = "wishlist:remove";
	/** @static @constant {string} Event triggered when the wishlist is cleared */
	static wishlistClear = "wishlist:clear";
	/** @static @constant {string} Event triggered when a wishlist operation fails */
	static wishlistError = "wishlist:error";
}

/**
 * Event fired when a variant is selected
 * @extends {Event}
 */
export class VariantSelectedEvent extends Event {
	/**
	 * Creates a new VariantSelectedEvent
	 * @param {Object} resource - The new variant object
	 * @param {string} resource.id - The id of the variant
	 */
	constructor(resource) {
		super(ThemeEvents.variantSelected, { bubbles: true });
		this.detail = {
			resource,
		};
	}
}

/**
 * Event fired after a variant is updated
 * @extends {Event}
 */
export class VariantUpdateEvent extends Event {
	/**
	 * Creates a new VariantUpdateEvent
	 * @param {Object} resource - The new variant object
	 * @param {string} resource.id - The id of the variant
	 * @param {boolean} resource.available - Whether the variant is available
	 * @param {boolean} resource.inventory_management - Whether the variant has inventory management
	 * @param {string} [resource.sku] - The SKU of the variant
	 * @param {Object} [resource.featured_media] - The featured media of the variant
	 * @param {string} [resource.featured_media.id] - The id of the featured media
	 * @param {Object} [resource.featured_media.preview_image] - The preview image of the featured media
	 * @param {string} [resource.featured_media.preview_image.src] - The src URL of the preview image
	 * @param {string} sourceId - The id of the element the action was triggered from
	 * @param {Object} data - Additional event data
	 * @param {Document} data.html - The new document fragment for the variant
	 * @param {string} data.productId - The product ID of the updated variant, used to ensure the correct product form is updated
	 * @param {Object} [data.newProduct] - If a new product was loaded as part of the variant update (combined listing)
	 * @param {string} data.newProduct.id - The id of the new product
	 * @param {string} data.newProduct.url - The url of the new product
	 */
	constructor(resource, sourceId, data) {
		super(ThemeEvents.variantUpdate, { bubbles: true });
		this.detail = {
			resource: resource || null,
			sourceId,
			data: {
				html: data.html,
				productId: data.productId,
				newProduct: data.newProduct,
			},
		};
	}
}

/**
 * Event class for cart additions
 * @extends {Event}
 */
export class CartAddEvent extends Event {
	/**
	 * Creates a new CartAddEvent
	 * @param {Object} [resource] - The new cart object
	 * @param {string} [sourceId] - The id of the element the action was triggered from
	 * @param {Object} [data] - Additional event data
	 * @param {boolean} [data.didError] - Whether the cart operation failed
	 * @param {string} [data.source] - The source of the cart update
	 * @param {string} [data.productId] - The id of the product card that was updated
	 * @param {number} [data.itemCount] - The number of items in the cart
	 * @param {string} [data.variantId] - The id of the product variant that was added
	 * @param {Record<string, string>} [data.sections] - The sections affected by the cart operation
	 */
	constructor(resource, sourceId, data) {
		super(CartAddEvent.eventName, { bubbles: true });
		this.detail = {
			resource,
			sourceId,
			data: {
				...data,
			},
		};
	}

	static eventName = ThemeEvents.cartUpdate;
}

/**
 * Event class for cart updates
 * @extends {Event}
 */
export class CartUpdateEvent extends Event {
	/**
	 * Creates a new CartUpdateEvent
	 * @param {Object} resource - The new cart object
	 * @param {string} sourceId - The id of the element the action was triggered from
	 * @param {Object} [data] - Additional event data
	 * @param {boolean} [data.didError] - Whether the cart operation failed
	 * @param {string} [data.source] - The source of the cart update
	 * @param {string} [data.productId] - The id of the product card that was updated
	 * @param {number} [data.itemCount] - The number of items in the cart
	 * @param {string} [data.variantId] - The id of the product variant that was updated
	 * @param {Record<string, string>} [data.sections] - The sections affected by the cart operation
	 */
	constructor(resource, sourceId, data) {
		super(ThemeEvents.cartUpdate, { bubbles: true });
		this.detail = {
			resource,
			sourceId,
			data: {
				...data,
			},
		};
	}
}

/**
 * Event class for cart errors
 * @extends {Event}
 */
export class CartErrorEvent extends Event {
	/**
	 * Creates a new CartErrorEvent
	 * @param {string} sourceId - The id of the element the action was triggered from
	 * @param {string} message - A message from the server response
	 * @param {Object} description - Description from the server response
	 * @param {Object} errors - Errors from the server response
	 */
	constructor(sourceId, message, description, errors) {
		super(ThemeEvents.cartError, { bubbles: true });
		this.detail = {
			sourceId,
			data: {
				message,
				errors,
				description,
			},
		};
	}
}

/**
 * Event class for quantity-selector updates
 * @extends {Event}
 */
export class QuantitySelectorUpdateEvent extends Event {
	/**
	 * Creates a new QuantitySelectorUpdateEvent
	 * @param {number} quantity - Quantity value
	 * @param {number} [cartLine] - The id of the updated cart line
	 */
	constructor(quantity, cartLine) {
		super(ThemeEvents.quantitySelectorUpdate, { bubbles: true });
		this.detail = {
			quantity,
			cartLine,
		};
	}
}

/**
 * Event class for quantity-selector updates
 * @extends {Event}
 */
export class DiscountUpdateEvent extends Event {
	/**
	 * Creates a new DiscountUpdateEvent
	 * @param {Object} resource - The new cart object
	 * @param {string} sourceId - The id of the element the action was triggered from
	 */
	constructor(resource, sourceId) {
		super(ThemeEvents.discountUpdate, { bubbles: true });
		this.detail = {
			resource,
			sourceId,
		};
	}
}

export class CompareAddEvent extends Event {
	constructor(handle, handles) {
		super(ThemeEvents.compareAdd, { bubbles: true });
		this.detail = {
			handle,
			handles,
		};
	}
}

export class CompareRemoveEvent extends Event {
	constructor(handle, handles) {
		super(ThemeEvents.compareRemove, { bubbles: true });
		this.detail = {
			handle,
			handles,
		};
	}
}

export class CompareActivateEvent extends Event {
	constructor() {
		super(ThemeEvents.compareActivate, { bubbles: true });
	}
}

export class CompareDeactivateEvent extends Event {
	constructor() {
		super(ThemeEvents.compareDeactivate, { bubbles: true });
	}
}

export class LazyVideoLoadedEvent extends Event {
	constructor(sourceId, data) {
		super(ThemeEvents.lazyVideoLoaded, { bubbles: true });
		this.detail = {
			sourceId,
			data,
		};
	}
}

/**
 * Event class for wishlist updates
 * @extends {Event}
 */
export class WishlistUpdateEvent extends Event {
	/**
	 * Creates a new WishlistUpdateEvent
	 * @param {string[]} handles - The current list of product handles in the wishlist
	 * @param {Object} [data] - Additional event data
	 */
	constructor(handles, data) {
		super(ThemeEvents.wishlistUpdate, { bubbles: true });
		this.detail = {
			handles,
			data: {
				...data,
			},
		};
	}
}

/**
 * Event class for wishlist additions
 * @extends {Event}
 */
export class WishlistAddEvent extends Event {
	/**
	 * Creates a new WishlistAddEvent
	 * @param {string} handle - The product handle that was added
	 * @param {string[]} handles - The current list of all handles in the wishlist
	 */
	constructor(handle, handles) {
		super(ThemeEvents.wishlistAdd, { bubbles: true });
		this.detail = {
			handle,
			handles,
		};
	}
}

/**
 * Event class for wishlist removals
 * @extends {Event}
 */
export class WishlistRemoveEvent extends Event {
	/**
	 * Creates a new WishlistRemoveEvent
	 * @param {string} handle - The product handle that was removed
	 * @param {string[]} handles - The current list of all handles in the wishlist
	 */
	constructor(handle, handles) {
		super(ThemeEvents.wishlistRemove, { bubbles: true });
		this.detail = {
			handle,
			handles,
		};
	}
}

/**
 * Event class for wishlist clear
 * @extends {Event}
 */
export class WishlistClearEvent extends Event {
	/**
	 * Creates a new WishlistClearEvent
	 */
	constructor() {
		super(ThemeEvents.wishlistClear, { bubbles: true });
		this.detail = {
			handles: [],
		};
	}
}

/**
 * Event class for wishlist errors
 * @extends {Event}
 */
export class WishlistErrorEvent extends Event {
	/**
	 * Creates a new WishlistErrorEvent
	 * @param {string} action - The action that failed (add, remove, clear)
	 * @param {string} handle - The product handle involved (if applicable)
	 * @param {Error} error - The error that occurred
	 */
	constructor(action, handle, error) {
		super(ThemeEvents.wishlistError, { bubbles: true });
		this.detail = {
			action,
			handle,
			error,
		};
	}
}
